import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {animate, state, style, transition, trigger} from '@angular/animations';

@Component({
    selector: 'ui-balloon',
    templateUrl: './ui-balloon.component.html',
    styleUrls: ['./ui-balloon.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
    animations: [
        trigger('balloonAnimation', [
            state('void', style({opacity: 0, transform: 'scale(0.8)'})),
            state('*', style({opacity: 1, transform: 'scale(1)'})),
            transition('void => *', [
                animate('300ms cubic-bezier(0.25, 0.8, 0.25, 1)')
            ]),
            transition('* => void', [
                animate('200ms cubic-bezier(0.25, 0.8, 0.25, 1)', style({opacity: 0, transform: 'scale(0.8)'}))
            ])
        ])
    ]
})
export class UiBalloonComponent implements OnInit, OnDestroy {
    @Input() content: string = '';
    @Input() animation: 'waves' | 'bounce' | 'none' = 'waves';
    @Input() color: 'primary' | 'secondary' | 'warning' | 'danger' | 'info' = 'primary';
    @Input() side: 'top' | 'left' | 'right' | 'bottom' = 'top';
    @Input() arrowPosition: 'left' | 'center' | 'right' = 'center';
    @Input() dismissable: boolean = false;
    @Input() expandable: boolean = false;
    @Input() expandedContent: string = '';

    private _balloon: ElementRef;

    @ViewChild('balloon')
    set balloon(value: ElementRef) {
        this._balloon = value;
    }

    get balloon(): ElementRef {
        return this._balloon;
    }

    target: HTMLElement;
    isVisible: boolean = false;
    isExpanded: boolean = false;
    isOffScreen: boolean = false;
    actualSide: 'top' | 'left' | 'right' | 'bottom' = 'top';

    private resizeObserver: ResizeObserver;
    private intersectionObserver: IntersectionObserver;
    private animationFrameId: number;

    constructor(private el: ElementRef, private renderer: Renderer2, private cdr: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        this.actualSide = this.side;
    }

    ngOnDestroy(): void {
        this.stopPositionUpdateLoop();
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
        }
    }

    setTarget(target: HTMLElement) {
        this.target = target;
        this.setupObservers();
        this.updatePosition();
    }

    show() {
        this.isVisible = true;
        this.cdr.detectChanges();
        this.startPositionUpdateLoop();
    }

    hide() {
        this.isVisible = false;
        this.isExpanded = false;
        this.stopPositionUpdateLoop();
        this.cdr.detectChanges();
    }

    toggleExpand() {
        if (this.expandable) {
            this.isExpanded = !this.isExpanded;
            this.cdr.detectChanges();
        }
    }

    dismiss() {
        this.hide();
        // Emit event if needed
    }

    private startPositionUpdateLoop() {
        this.stopPositionUpdateLoop();
        const loop = () => {
            if (!this.isOffScreen) {
                this.updatePosition();
            }
            if (this.isVisible) {
                this.animationFrameId = requestAnimationFrame(loop);
            }
        };
        this.animationFrameId = requestAnimationFrame(loop);
    }

    private stopPositionUpdateLoop() {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
    }

    private setupObservers() {
        if (this.target) {
            this.resizeObserver = new ResizeObserver(() => {
                if (this.isVisible && !this.isOffScreen) this.updatePosition();
            });
            this.resizeObserver.observe(this.target);

            this.intersectionObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    this.isOffScreen = !entry.isIntersecting;
                    if (entry.isIntersecting && this.isVisible) {
                        this.updatePosition();
                    }
                    this.cdr.detectChanges();
                });
            });
            this.intersectionObserver.observe(this.target);
        }
    }

    private updatePosition() {
        if (!this.target || !this.balloon) return;

        const targetRect = this.target.getBoundingClientRect();

        if (targetRect.width === 0 && targetRect.height === 0) {
            return;
        }

        const balloonRect = this.balloon.nativeElement.getBoundingClientRect();

        if (balloonRect.width === 0 && balloonRect.height === 0) {
            return;
        }

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let top = 0;
        let left = 0;
        let side = this.side;

        // Check if balloon fits in the preferred side
        if (side === 'top' && targetRect.top - balloonRect.height < 0) side = 'bottom';
        else if (side === 'bottom' && targetRect.bottom + balloonRect.height > viewportHeight) side = 'top';
        else if (side === 'left' && targetRect.left - balloonRect.width < 0) side = 'right';
        else if (side === 'right' && targetRect.right + balloonRect.width > viewportWidth) side = 'left';

        this.actualSide = side;
        this.cdr.detectChanges();

        switch (this.actualSide) {
            case 'top':
                top = targetRect.top - balloonRect.height - 10;
                left = targetRect.left + (targetRect.width / 2) - (balloonRect.width / 2);
                break;
            case 'bottom':
                top = targetRect.bottom + 10;
                left = targetRect.left + (targetRect.width / 2) - (balloonRect.width / 2);
                break;
            case 'left':
                top = targetRect.top + (targetRect.height / 2) - (balloonRect.height / 2);
                left = targetRect.left - balloonRect.width - 10;
                break;
            case 'right':
                top = targetRect.top + (targetRect.height / 2) - (balloonRect.height / 2);
                left = targetRect.right + 10;
                break;
        }

        // Adjust horizontal position to keep within viewport
        if (left < 10) left = 10;
        if (left + balloonRect.width > viewportWidth - 10) left = viewportWidth - balloonRect.width - 10;

        // Adjust vertical position to keep within viewport
        if (top < 10) top = 10;
        if (top + balloonRect.height > viewportHeight - 10) top = viewportHeight - balloonRect.height - 10;

        this.renderer.setStyle(this.balloon.nativeElement, 'top', `${top}px`);
        this.renderer.setStyle(this.balloon.nativeElement, 'left', `${left}px`);
    }
}
